/**
 * POA&M items — V102-6-3 / ADR-0058 §4; V103-0-4: any program whose manifest
 * declares `features.poam`. Eligibility comes from the period's rule-set
 * family (`POAM_POLICIES`); a family with no POA&M rules restricts nothing.
 * The CMMC rules below:
 *
 *   - **Open** from a reviewed not_met determination. The requirement's
 *     POA&M eligibility comes from the period's rule set (pinned at Audit,
 *     else current): an ineligible or never-eligible requirement is refused,
 *     and the refusal says it blocks conditional status. Points, eligibility,
 *     the partial case and the rule set are snapshotted on the item.
 *   - **Clock** (32 CFR 170.21(b); migration 312a, owner 2026-10-05): one clock
 *     per assessment, starting at the CONDITIONAL STATUS DATE — when the
 *     initial affirmation at conditional status is signed
 *     (`startPoamClock`, called by finalizeAffirmation). closeout_due_at =
 *     that date + rule-set `poam.closeout_days`. Before it an open item is
 *     *planned*: no clock, no due date, never expires. An item opened after
 *     the clock started takes the period's existing clock.
 *   - **Expire**: an open item past its due date becomes `expired`
 *     (`expirePoamItems`, also done on read by closePoamItem); the score then
 *     shows conditional status lapsed.
 *   - **Close** needs a reviewed met (or N/A) determination on the requirement
 *     and the requirement now met in readiness. The POA&M closeout is ONE
 *     assessment covering every item on the POA&M (32 CFR 170.21(a)(4)) with
 *     ONE affirmation at its completion (170.22(b)(2), verified 2026-10-05):
 *     items close one by one, and closing the LAST clocked open item of the
 *     period requests the single closeout affirmation from the named official,
 *     stamped on every clocked closed item. The affirmation gate (D-UX-1) is
 *     checked BEFORE that last item closes, so a refusal leaves it open. A
 *     planned item (fixed before status) closes on the reviewed Met alone.
 *   - **Export**: readable rows + OSCAL POA&M (`toOscalCmmcPoam`).
 * GovRAMP (V103-3-2, rule set `poam.timeframes`): no eligibility rule; each
 * item gets its own clock at opening: due = opened + the risk's timeframe
 * (High 30 / Moderate 90 / Low 180 days in v1), or the CISA KEV due date when
 * one is given. A late item stays open (lateness is a ConMon matter, V103-3-5),
 * never expires, and closing it requests no affirmation.
 * Every read and write is scoped by workspace_id (the session's workspace,
 * never a URL-supplied id); another tenant's item reads as not found.
 */
import { generateRandomId } from '../utils/audit-crypto.js';
import { getProgramManifest } from '../programs/manifests.js';
import { addTimeframe } from './certification-service.js';
import { getAssessmentDetermination } from './determination-service.js';
import { computeObjectiveReadiness } from './objective-readiness-service.js';
import { CMMC_RULE_FAMILY, parseCmmcRules, parseGovrampRules, parseRuleSet, resolveRuleSetForPeriod } from './program-rule-set-service.js';
import { GOVRAMP_RULE_FAMILY, GovrampRulesError, poamDueAt } from './govramp-program-rules.js';
import { poamEligibility, pointsFor, ruleFor } from './cmmc-scoring-service.js';
import { computeAffirmationGate, createAffirmation } from './affirmation-service.js';
import { toOscalCmmcPoam } from './component-export-service.js';
export class PoamError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'PoamError';
    }
}
export async function getPoamItem(db, workspaceId, poamId) {
    const r = await db.query(`SELECT * FROM poam_items WHERE poam_id = ? AND workspace_id = ? LIMIT 1`, [poamId, workspaceId]);
    return r.results[0] ?? null;
}
export async function listPoamItems(db, workspaceId, f = {}) {
    const where = ['workspace_id = ?'];
    const params = [workspaceId];
    if (f.periodId) {
        where.push('period_id = ?');
        params.push(f.periodId);
    }
    if (f.status) {
        where.push('status = ?');
        params.push(f.status);
    }
    const r = await db.query(`SELECT * FROM poam_items WHERE ${where.join(' AND ')} ORDER BY (closeout_due_at IS NULL), closeout_due_at, opened_at, poam_id`, params);
    return r.results;
}
async function poamPeriod(db, workspaceId, periodId) {
    const r = await db.query(`SELECT p.period_id, p.profile_id, p.period_start, p.period_end, c.program,
            COALESCE(p.program_options_json, c.program_options_json) AS program_options_json
       FROM audit_periods p JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [periodId, workspaceId]);
    const p = r.results[0];
    if (!p)
        throw new PoamError('not_found', 'Audit period not found.');
    const manifest = getProgramManifest(p.program);
    if (!manifest?.features.poam || !manifest.scoring.rulesRef)
        throw new PoamError('invalid', 'This program does not use POA&M items.');
    return { period: p, manifest, family: manifest.scoring.rulesRef };
}
async function requirementOf(content, objectiveId) {
    const r = await content.query(`SELECT requirement_id FROM assessment_objectives WHERE objective_id = ? LIMIT 1`, [objectiveId]);
    const id = r.results[0]?.requirement_id;
    if (!id)
        throw new PoamError('invalid', `Objective ${objectiveId} is not in the catalog.`);
    return id;
}
const cmmcPoamPolicy = {
    precheck(options) {
        if (options.level === '1')
            throw new PoamError('ineligible', 'CMMC Level 1 has no POA&M: every requirement must be met.');
    },
    admit(ruleSet, requirementId, condition) {
        const rules = parseCmmcRules(ruleSet);
        const rule = ruleFor(rules, requirementId);
        if (!rule)
            throw new PoamError('invalid', `${requirementId} is not in rule set ${ruleSet.rule_set_id}.`);
        let points;
        try {
            points = pointsFor(rules, requirementId, condition);
        }
        catch (e) {
            throw new PoamError('invalid', e.message);
        }
        const ref = requirementId.replace(/^cmmc-l2:/, '');
        const eligibility = poamEligibility(rules, requirementId, condition);
        if (eligibility === 'never') {
            throw new PoamError('ineligible', `${ref} can never be on a POA&M; while it is not met, the period cannot reach conditional status.`);
        }
        if (eligibility === 'ineligible') {
            const why = rule.poam_eligible === 'exception'
                ? `only under its exception (${rules.poam.exceptions.find((e) => e.requirement === requirementId)?.when_condition})`
                : `only ${rules.poam.eligible_points}-point requirements are eligible and it is worth ${rule.points}`;
            throw new PoamError('ineligible', `${ref} is not POA&M-eligible (${why}); while it is not met, the period cannot reach conditional status.`);
        }
        return { points, eligibility };
    },
    lapses: true,
    closeoutAffirmation: true,
};
const govrampPoamPolicy = {
    precheck() { },
    admit(ruleSet) {
        try {
            parseGovrampRules(ruleSet);
        }
        catch (e) {
            throw new PoamError('invalid', e.message);
        }
        return { points: 0, eligibility: 'eligible' };
    },
    itemDue(ruleSet, input) {
        try {
            return poamDueAt(parseGovrampRules(ruleSet), input).due_at;
        }
        catch (e) {
            if (e instanceof GovrampRulesError)
                throw new PoamError('invalid', e.message);
            throw e;
        }
    },
    lapses: false,
    closeoutAffirmation: false,
};
/** A family without POA&M rules: every unmet requirement may go on the POA&M, with no points. */
const unrestrictedPoamPolicy = {
    precheck() { },
    admit(ruleSet) {
        try {
            parseRuleSet(ruleSet);
        }
        catch (e) {
            throw new PoamError('invalid', e.message);
        }
        return { points: 0, eligibility: 'eligible' };
    },
    lapses: true,
    closeoutAffirmation: true,
};
const POAM_POLICIES = new Map([
    [CMMC_RULE_FAMILY, cmmcPoamPolicy],
    [GOVRAMP_RULE_FAMILY, govrampPoamPolicy],
]);
/** `govramp@v1` -> `govramp` (ruleSetIdFor). */
const familyOfRuleSet = (ruleSetId) => ruleSetId.replace(/@v\d+$/, '');
/** Families whose past-due items never expire. */
const NON_LAPSING_FAMILIES = [...POAM_POLICIES].filter(([, p]) => !p.lapses).map(([f]) => f).sort();
function poamPolicyFor(family) {
    return POAM_POLICIES.get(family) ?? unrestrictedPoamPolicy;
}
function programOptions(json) {
    try {
        const o = JSON.parse(json || '{}');
        return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
    }
    catch {
        return {};
    }
}
export async function openPoamItem(db, input) {
    const content = input.contentDb ?? db;
    const det = await getAssessmentDetermination(db, input.workspaceId, input.determinationId);
    if (!det)
        throw new PoamError('not_found', 'Determination not found.');
    if (det.state !== 'reviewed' || det.result !== 'not_met') {
        throw new PoamError('invalid', 'A POA&M item opens from a reviewed Not met determination.');
    }
    const { period, manifest, family } = await poamPeriod(db, input.workspaceId, det.period_id);
    const policy = poamPolicyFor(family);
    policy.precheck(programOptions(period.program_options_json));
    const requirementId = await requirementOf(content, det.objective_id);
    const resolved = await resolveRuleSetForPeriod(db, { workspaceId: input.workspaceId, periodId: det.period_id, programKey: period.program, contentDb: input.contentDb });
    if (!resolved)
        throw new PoamError('invalid', `No approved ${manifest.label} rule set yet: approve the ${manifest.label} program rules in the review queue.`);
    const condition = input.partialCondition?.trim() || null;
    const { points, eligibility } = policy.admit(resolved.ruleSet, requirementId, condition);
    const ref = requirementId.replace(/^cmmc-l2:/, '');
    const dup = await db.query(`SELECT poam_id FROM poam_items WHERE workspace_id = ? AND period_id = ? AND requirement_id = ? AND status = 'open' LIMIT 1`, [input.workspaceId, det.period_id, requirementId]);
    if (dup.results.length)
        throw new PoamError('conflict', `${ref} already has an open POA&M item in this period.`);
    if (input.ownerUserId) {
        const m = await db.query(`SELECT 1 AS ok FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [input.workspaceId, input.ownerUserId]);
        if (!m.results.length)
            throw new PoamError('invalid', 'The owner must be an active member of the workspace.');
    }
    const now = input.now ?? new Date();
    const opened = now.toISOString();
    let clock;
    if (policy.itemDue) {
        // Per-item clock from opening (GovRAMP remediation timeframes).
        let findingSeverity = null;
        if (!input.risk && input.findingId) {
            const f = await db.query(`SELECT severity FROM findings WHERE finding_id = ? AND workspace_id = ? LIMIT 1`, [input.findingId, input.workspaceId]);
            if (!f.results.length)
                throw new PoamError('invalid', 'Finding not found.');
            findingSeverity = f.results[0].severity;
        }
        const due = policy.itemDue(resolved.ruleSet, { openedAt: opened, risk: input.risk?.trim() || null, findingSeverity, kevDueAt: input.kevDueAt?.trim() || null });
        clock = { clock_started_at: opened, clock_affirmation_id: null, closeout_due_at: due };
    }
    else {
        // Planned until conditional status; after it, the period's one clock.
        clock = await periodClock(db, input.workspaceId, det.period_id);
        if (clock && clock.closeout_due_at <= opened) {
            throw new PoamError('expired', 'The closeout date for this assessment has passed: conditional status lapsed, so no item can be added.');
        }
    }
    const poamId = input.poamId ?? generateRandomId('poam');
    await db.execute(`INSERT INTO poam_items (
       poam_id, workspace_id, profile_id, period_id, requirement_id, objective_id, determination_id, finding_id,
       rule_set_id, rule_set_basis, points_snapshot, eligibility_snapshot, partial_condition, plan, owner_user_id,
       opened_by, opened_at, clock_started_at, clock_affirmation_id, closeout_due_at, status, created_at, updated_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'open', ?, ?)`, [
        poamId, input.workspaceId, period.profile_id, det.period_id, requirementId, det.objective_id, det.determination_id,
        input.findingId ?? null, resolved.ruleSet.rule_set_id, resolved.basis, points, eligibility, condition,
        input.plan?.trim() || null, input.ownerUserId ?? null, input.openedBy, opened,
        clock?.clock_started_at ?? null, clock?.clock_affirmation_id ?? null, clock?.closeout_due_at ?? null, opened, opened,
    ]);
    return (await getPoamItem(db, input.workspaceId, poamId));
}
/** The period's POA&M clock once conditional status started it (any item carries it), else null. */
async function periodClock(db, workspaceId, periodId) {
    const r = await db.query(`SELECT clock_started_at, clock_affirmation_id, closeout_due_at FROM poam_items
      WHERE workspace_id = ? AND period_id = ? AND clock_started_at IS NOT NULL
      ORDER BY clock_started_at ASC, poam_id ASC LIMIT 1`, [workspaceId, periodId]);
    return r.results[0] ?? null;
}
/**
 * Start the period's closeout clock at the conditional status date (32 CFR
 * 170.21(b)): every open, planned item gets clock_started_at = that date and
 * closeout_due_at = date + rule-set closeout days. Items that already carry a
 * clock keep it (the trigger refuses a move). Returns the ids started.
 */
export async function startPoamClock(db, input) {
    const due = addTimeframe(new Date(input.conditionalAt), input.closeoutDays, 'days').toISOString();
    const planned = await db.query(`SELECT poam_id FROM poam_items WHERE workspace_id = ? AND period_id = ? AND status = 'open' AND clock_started_at IS NULL ORDER BY poam_id`, [input.workspaceId, input.periodId]);
    if (!planned.results.length)
        return [];
    await db.execute(`UPDATE poam_items SET clock_started_at = ?, clock_affirmation_id = ?, closeout_due_at = ?, updated_at = ?
      WHERE workspace_id = ? AND period_id = ? AND status = 'open' AND clock_started_at IS NULL`, [input.conditionalAt, input.affirmationId, due, input.conditionalAt, input.workspaceId, input.periodId]);
    return planned.results.map((r) => r.poam_id);
}
/** Open items past their closeout date → expired. Returns the ids moved. Planned items (no clock) never expire. */
export async function expirePoamItems(db, input) {
    const now = (input.now ?? new Date()).toISOString();
    // Families whose late items stay open (GovRAMP) are skipped by rule-set id prefix.
    // d1-in-list: bounded — one placeholder per registered POA&M family (a code constant, ≤ a handful)
    const skip = NON_LAPSING_FAMILIES.map(() => ` AND rule_set_id NOT LIKE ?`).join('');
    const skipParams = NON_LAPSING_FAMILIES.map((f) => `${f}@%`);
    const due = await db.query(`SELECT poam_id FROM poam_items WHERE workspace_id = ? AND status = 'open' AND closeout_due_at IS NOT NULL AND closeout_due_at <= ?${skip} ORDER BY poam_id`, [input.workspaceId, now, ...skipParams]);
    if (!due.results.length)
        return [];
    await db.execute(`UPDATE poam_items SET status = 'expired', expired_at = ?, updated_at = ? WHERE workspace_id = ? AND status = 'open' AND closeout_due_at IS NOT NULL AND closeout_due_at <= ?${skip}`, [now, now, input.workspaceId, now, ...skipParams]);
    return due.results.map((r) => r.poam_id);
}
/**
 * Close an open item: the requirement must be met now (readiness, reviewed
 * determinations only), with a reviewed met / N/A determination on one of its
 * objectives as the closeout evidence. Then the closeout affirmation is
 * requested from the official.
 */
export async function closePoamItem(db, input) {
    const content = input.contentDb ?? db;
    const item = await getPoamItem(db, input.workspaceId, input.poamId);
    if (!item)
        throw new PoamError('not_found', 'POA&M item not found.');
    const now = input.now ?? new Date();
    const policy = poamPolicyFor(familyOfRuleSet(item.rule_set_id));
    if (policy.lapses && item.status === 'open' && item.closeout_due_at !== null && item.closeout_due_at <= now.toISOString()) {
        await expirePoamItems(db, { workspaceId: input.workspaceId, now });
        throw new PoamError('expired', 'The closeout date has passed: the item expired and conditional status lapsed.');
    }
    if (item.status !== 'open')
        throw new PoamError('conflict', `The item is ${item.status}.`);
    if (input.closedBy === input.officialUserId) {
        throw new PoamError('invalid', 'The person closing the item cannot also be the affirming official (ADR-0046).');
    }
    const readiness = await computeObjectiveReadiness(db, { workspaceId: input.workspaceId, periodId: item.period_id, contentDb: input.contentDb });
    const req = readiness.requirements.find((r) => r.requirement_id === item.requirement_id);
    if (!req || req.status !== 'met') {
        throw new PoamError('conflict', 'The requirement is not met yet: record and review a Met determination on its objectives first.');
    }
    const objs = await content.query(`SELECT objective_id FROM assessment_objectives WHERE requirement_id = ? AND status = 'active'`, [item.requirement_id]);
    const counted = new Set(req.objectives.map((o) => o.determination_id).filter(Boolean));
    for (const o of req.objectives)
        for (const a of o.assets ?? [])
            if (a.determination_id)
                counted.add(a.determination_id);
    const ids = objs.results.map((o) => o.objective_id);
    let closeout = null;
    if (ids.length) {
        // d1-in-list: bounded — one requirement's objectives (SP 800-171A: at most 12), plus 2 params
        const dets = await db.query(`SELECT determination_id FROM determinations
        WHERE workspace_id = ? AND period_id = ? AND state = 'reviewed' AND result IN ('met', 'not_applicable')
          AND objective_id IN (${ids.map(() => '?').join(', ')})
        ORDER BY reviewed_at DESC, determination_id DESC`, [input.workspaceId, item.period_id, ...ids]);
        closeout = dets.results.find((d) => counted.has(d.determination_id))?.determination_id ?? null;
    }
    if (!closeout)
        throw new PoamError('conflict', 'No reviewed Met determination on this requirement to close it with.');
    // A per-item clock (GovRAMP) has no closeout assessment to complete.
    const clocked = item.closeout_due_at !== null && policy.closeoutAffirmation;
    const othersOpen = clocked
        ? (await db.query(`SELECT COUNT(*) AS n FROM poam_items WHERE workspace_id = ? AND period_id = ? AND status = 'open' AND closeout_due_at IS NOT NULL AND poam_id <> ?`, [input.workspaceId, item.period_id, item.poam_id])).results[0]?.n ?? 0
        : 0;
    const completesCloseout = clocked && Number(othersOpen) === 0;
    if (completesCloseout) {
        // Check the affirmation gate first: a refused closeout affirmation must not leave a closed item behind.
        const gate = await computeAffirmationGate(db, { workspaceId: input.workspaceId, periodId: item.period_id, contentDb: input.contentDb, now });
        if (!gate.allowed_kinds.includes('poam_closeout')) {
            throw new PoamError('conflict', gate.finished
                ? 'The closeout affirmation opens only at CMMC status.'
                : 'The closeout affirmation needs a finished self-assessment (nothing unassessed or awaiting review, SSP in place, snapshot pinned).');
        }
    }
    const at = now.toISOString();
    const res = await db.execute(`UPDATE poam_items SET status = 'closed', closed_at = ?, closed_by = ?, closeout_determination_id = ?, updated_at = ?
      WHERE poam_id = ? AND workspace_id = ? AND status = 'open'`, [at, input.closedBy, closeout, at, item.poam_id, input.workspaceId]);
    if ((res.meta?.changes ?? 1) === 0)
        throw new PoamError('conflict', 'The item changed while it was being closed.');
    // Planned items, and clocked items while others stay open, have nothing to affirm yet.
    if (!completesCloseout)
        return { item: (await getPoamItem(db, input.workspaceId, item.poam_id)), affirmation: null };
    // The last clocked item closed: the closeout assessment is complete → one affirmation, on every clocked item.
    const affirmation = await createAffirmation(db, {
        workspaceId: input.workspaceId, profileId: item.profile_id, periodId: item.period_id, kind: 'poam_closeout', poamId: item.poam_id,
        officialUserId: input.officialUserId, preparerUserId: input.closedBy, sign: input.sign, contentDb: input.contentDb, now,
    });
    await db.execute(`UPDATE poam_items SET closeout_affirmation_id = ?, updated_at = ?
      WHERE workspace_id = ? AND period_id = ? AND status = 'closed' AND closeout_due_at IS NOT NULL AND closeout_affirmation_id IS NULL`, [affirmation.affirmation_id, at, input.workspaceId, item.period_id]);
    return { item: (await getPoamItem(db, input.workspaceId, item.poam_id)), affirmation };
}
/** The period's POA&M as readable rows and an OSCAL 1.2.3 POA&M (workspace-scoped). */
export async function buildCmmcPoamDocument(db, input) {
    const content = input.contentDb ?? db;
    const { period } = await poamPeriod(db, input.workspaceId, input.periodId);
    const now = input.now ?? new Date();
    const items = await listPoamItems(db, input.workspaceId, { periodId: input.periodId });
    const resolved = await resolveRuleSetForPeriod(db, { workspaceId: input.workspaceId, periodId: input.periodId, programKey: period.program, contentDb: input.contentDb });
    const titles = new Map();
    for (const it of items) {
        if (titles.has(it.requirement_id))
            continue;
        const t = await content.query(`SELECT title FROM framework_requirements WHERE requirement_id = ? LIMIT 1`, [it.requirement_id]);
        titles.set(it.requirement_id, t.results[0]?.title ?? '');
    }
    const ws = await db.query(`SELECT name FROM workspaces WHERE workspace_id = ? LIMIT 1`, [input.workspaceId]);
    const rows = items.map((i) => ({
        poam_id: i.poam_id, requirement_id: i.requirement_id, requirement_title: titles.get(i.requirement_id) || null,
        points: i.points_snapshot, eligibility: i.eligibility_snapshot, partial_condition: i.partial_condition,
        // Past due but not swept yet reads as expired: the clock is the record.
        status: i.status === 'open' && i.closeout_due_at !== null && i.closeout_due_at <= now.toISOString() ? 'expired' : i.status,
        opened_at: i.opened_at, closeout_due_at: i.closeout_due_at, closed_at: i.closed_at, plan: i.plan, determination_id: i.determination_id,
    }));
    const counts = { open: 0, closed: 0, expired: 0 };
    for (const r of rows)
        counts[r.status]++;
    const exportDoc = {
        organization: ws.results[0]?.name ?? input.workspaceId,
        profile_id: period.profile_id,
        period: { period_id: period.period_id, period_start: period.period_start, period_end: period.period_end },
        rule_set_id: resolved?.ruleSet.rule_set_id ?? 'none',
        items: rows,
    };
    return {
        schema: 'gacpro/cmmc-poam@1',
        kind: 'cmmc_poam',
        generated_at: now.toISOString(),
        profile_id: period.profile_id,
        period: exportDoc.period,
        rule_set_id: resolved?.ruleSet.rule_set_id ?? null,
        readable: {
            items: rows.map((r) => ({
                // Planned items (no clock before conditional status) have no countdown.
                ...r, days_remaining: r.status === 'open' && r.closeout_due_at ? Math.ceil((Date.parse(r.closeout_due_at) - now.getTime()) / 86_400_000) : null,
            })),
            counts,
        },
        oscal: toOscalCmmcPoam(exportDoc, { now: () => now, ...input.oscal }),
    };
}
//# sourceMappingURL=poam-service.js.map